'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useMemo, useRef, useState } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  CaretDownIcon,
  CaretRightIcon,
  CaretUpIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import { columnTooltip, type ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import type { FilterState, ColumnFilterValue } from '@/shared/mobile-service/lib/where';
import { CellValue } from './renderers';
import { ColumnFilter } from './ColumnFilter';
import { NestedTable } from './NestedTable';
import { ContentPane } from './ContentPane';

export interface NestedTableConfig {
  label: string;
  columns: ResolvedColumn[];
}

interface DataTableProps {
  columns: ResolvedColumn[];
  rows: Record<string, unknown>[];
  idField: string;
  total: number;
  offset: number;
  limit: number;
  onOffsetChange: (offset: number) => void;
  onLimitChange: (limit: number) => void;
  sort: { field: string; direction: 'asc' | 'desc' } | null;
  onSortChange: (sort: { field: string; direction: 'asc' | 'desc' } | null) => void;
  filters: FilterState;
  onFilterChange: (field: string, value: ColumnFilterValue | undefined) => void;
  isFetching: boolean;
  /** Nested child-table config keyed by the parent column field. */
  nested?: Record<string, NestedTableConfig>;
  /** Fields shown in the row detail pane (typically every artifact field). */
  contentFields?: ResolvedColumn[];
  contentTitle?: (row: Record<string, unknown>) => string;
  /** Currently focused row id, highlighted in the grid. */
  selectedId?: string | null;
  /** Extra row ids to highlight, such as every id in a multi-row citation. */
  highlightedIds?: Array<string | number>;
  /** Called with the clicked row's id. Set to route selection to a side panel. */
  onSelectRow?: (id: string) => void;
  /** Fill the parent instead of sizing the body against the viewport. */
  fillHeight?: boolean;
}

const PAGE_SIZES = [25, 50, 100, 200];
const ROW_HEIGHT = 40;
const DEFAULT_COL_WIDTH = 160;
const MIN_COL_WIDTH = 64;
const GUTTER_WIDTH_PX = 36; // 2.25rem
const NO_HIGHLIGHTS: Array<string | number> = [];

/**
 * Reusable schema-driven data grid. Built on ``@tanstack/react-table`` (core
 * row model) with ``@tanstack/react-virtual`` row virtualization for large
 * pages. Paging / sorting / filtering are all server-driven (manual) — the
 * parent owns the state and refetches; this component only renders and emits
 * intent. Supports expandable nested sub-tables, a content reading pane, and
 * drag-to-resize column widths.
 */
export function DataTable(props: DataTableProps) {
  const t = useTimePilotT();
  const {
    columns,
    rows,
    idField,
    total,
    offset,
    limit,
    onOffsetChange,
    onLimitChange,
    sort,
    onSortChange,
    filters,
    onFilterChange,
    isFetching,
    nested,
    contentFields,
    contentTitle,
    selectedId,
    highlightedIds = NO_HIGHLIGHTS,
    onSelectRow,
    fillHeight = false,
  } = props;

  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const [paneRow, setPaneRow] = useState<Record<string, unknown> | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef<{ field: string; startX: number; startWidth: number } | null>(
    null,
  );

  const highlighted = useMemo(() => {
    const ids = new Set(highlightedIds.map(String));
    if (selectedId != null) ids.add(selectedId);
    return ids;
  }, [highlightedIds, selectedId]);
  const hasNested = Boolean(nested && Object.keys(nested).length > 0);
  const hasContent = Boolean(contentFields && contentFields.length > 0);
  const isRowClickable = hasContent || Boolean(onSelectRow);
  const nestedFields = nested ? Object.keys(nested) : [];
  const showGutter = hasNested || hasContent;

  const tanstackColumns: ColumnDef<Record<string, unknown>>[] = columns.map((c) => ({
    id: c.field,
    accessorKey: c.field,
  }));

  const table = useReactTable({
    data: rows,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  const modelRows = table.getRowModel().rows;

  const rowVirtualizer = useVirtualizer({
    count: modelRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const resolvedWidths = useMemo(
    () =>
      columns.map((c) => columnWidths[c.field] ?? c.width ?? DEFAULT_COL_WIDTH),
    [columns, columnWidths],
  );

  const gridTemplate = [
    showGutter ? `${GUTTER_WIDTH_PX}px` : null,
    ...resolvedWidths.map((w) => `${w}px`),
  ]
    .filter(Boolean)
    .join(' ');

  const tableMinWidth =
    (showGutter ? GUTTER_WIDTH_PX : 0) + resolvedWidths.reduce((sum, w) => sum + w, 0);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const active = resizingRef.current;
      if (!active) return;
      const next = Math.max(MIN_COL_WIDTH, active.startWidth + (e.clientX - active.startX));
      setColumnWidths((prev) => ({ ...prev, [active.field]: next }));
    };
    const onUp = () => {
      if (!resizingRef.current) return;
      resizingRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startResize = (field: string, startWidth: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    resizingRef.current = { field, startX: e.clientX, startWidth };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const resetWidth = (field: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setColumnWidths((prev) => {
      if (!(field in prev)) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const toggleExpand = (id: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSort = (col: ResolvedColumn) => {
    if (!col.sortable) return;
    if (resizingRef.current) return;
    if (sort?.field !== col.field) {
      onSortChange({ field: col.field, direction: 'desc' });
    } else if (sort.direction === 'desc') {
      onSortChange({ field: col.field, direction: 'asc' });
    } else {
      onSortChange(null);
    }
  };

  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  return (
    <div
      className={`flex min-w-0 w-full flex-col gap-3 ${fillHeight ? 'min-h-0 flex-1' : ''}`}
    >
      <div
        className={`min-w-0 overflow-hidden rounded-lg border border-white/10 ${
          fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''
        }`}
      >
        <div
          className={`min-w-0 overflow-x-auto ${fillHeight ? 'flex min-h-0 flex-1 flex-col' : ''}`}
          data-testid="DataTableHScroll"
        >
          <div
            className={fillHeight ? 'flex min-h-0 flex-1 flex-col' : undefined}
            style={{ minWidth: tableMinWidth }}
          >
            {/* Header */}
            <div
              className="grid items-center border-b border-white/10 bg-white/5 text-xs uppercase tracking-wide text-white/65"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {showGutter && <div className="px-2 py-2.5" />}
              {columns.map((col, index) => {
                const width = resolvedWidths[index];
                return (
                  <div
                    key={col.field}
                    className="relative flex min-w-0 items-center gap-1 px-3 py-2.5"
                  >
                    <button
                      type="button"
                      className={`flex min-w-0 items-center gap-1 font-medium ${
                        col.sortable ? 'hover:text-white/90' : 'cursor-default'
                      } ${col.align === 'right' ? 'ml-auto' : ''}`}
                      onClick={() => toggleSort(col)}
                    >
                      <span className="truncate" title={columnTooltip(col)}>
                        {col.label}
                      </span>
                      {col.sortable && sort?.field === col.field && (
                        <span className="shrink-0 text-brand-blue-light">
                          {sort.direction === 'asc' ? (
                            <CaretUpIcon size={12} />
                          ) : (
                            <CaretDownIcon size={12} />
                          )}
                        </span>
                      )}
                    </button>
                    {col.filter && (
                      <span className={`shrink-0 ${col.align === 'right' ? '' : 'ml-auto'}`}>
                        <ColumnFilter
                          col={col}
                          value={filters[col.field]}
                          onChange={(v) => onFilterChange(col.field, v)}
                        />
                      </span>
                    )}
                    <div
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Resize ${col.label} column`}
                      title={t("viewers.dragToResizeDoubleClickToReset")}
                      className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-col-resize touch-none hover:bg-brand-blue-light/60 active:bg-brand-blue/70"
                      onMouseDown={(e) => startResize(col.field, width, e)}
                      onDoubleClick={(e) => resetWidth(col.field, e)}
                    />
                  </div>
                );
              })}
            </div>

            {/* Body (virtualized) */}
            <div
              ref={scrollRef}
              data-testid="DataTableVScroll"
              className={`${
                fillHeight ? 'min-h-0 flex-1' : 'max-h-[calc(100vh-20rem)]'
              } overflow-y-auto overflow-x-hidden`}
              style={{ opacity: isFetching ? 0.6 : 1 }}
            >
              {modelRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-white/50">{t("viewers.noRowsMatchTheCurrentFilters")}</div>
              ) : (
                <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const row = modelRows[virtualRow.index].original;
                    const rowId = (row[idField] as string | number) ?? virtualRow.index;
                    const isExpanded = expanded.has(rowId);
                    return (
                      <div
                        key={rowId}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        className="absolute left-0 top-0 w-full border-b border-white/5"
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                      >
                        <div
                          className={`grid items-center text-sm ${
                            isRowClickable ? 'cursor-pointer hover:bg-brand-blue/15' : ''
                          } ${
                            highlighted.has(String(rowId))
                              ? 'bg-brand-blue/15 ring-1 ring-inset ring-brand-blue-light/60'
                              : ''
                          }`}
                          style={{ gridTemplateColumns: gridTemplate, minHeight: ROW_HEIGHT }}
                          onClick={
                            isRowClickable
                              ? () => {
                                  // Selection routes the row to a side panel when the
                                  // caller wants it there; otherwise fall back to the
                                  // built-in reading pane.
                                  if (onSelectRow) onSelectRow(String(rowId));
                                  else setPaneRow(row);
                                }
                              : undefined
                          }
                        >
                          {showGutter && (
                            <div className="flex items-center justify-center px-2">
                              {hasNested ? (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(rowId);
                                  }}
                                  className="text-white/50 hover:text-white/80"
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  {isExpanded ? (
                                    <CaretDownIcon size={14} />
                                  ) : (
                                    <CaretRightIcon size={14} />
                                  )}
                                </button>
                              ) : (
                                <FileTextIcon size={14} className="text-white/40" />
                              )}
                            </div>
                          )}
                          {columns.map((col) => (
                            <div
                              key={col.field}
                              className={`min-w-0 truncate px-3 py-2 ${
                                col.align === 'right'
                                  ? 'text-right'
                                  : col.align === 'center'
                                    ? 'text-center'
                                    : ''
                              }`}
                            >
                              {col.render === 'nested_table' ? (
                                <NestedCount value={row[col.field]} />
                              ) : (
                                <CellValue col={col} value={row[col.field]} />
                              )}
                            </div>
                          ))}
                        </div>
                        {isExpanded && hasNested && (
                          <div className="bg-white/5">
                            {nestedFields.map((field) => {
                              const cfg = nested![field];
                              const childRows = Array.isArray(row[field])
                                ? (row[field] as Record<string, unknown>[])
                                : [];
                              return (
                                <NestedTable
                                  key={field}
                                  label={cfg.label}
                                  columns={cfg.columns}
                                  rows={childRows}
                                />
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex shrink-0 items-center justify-between text-sm text-white/70">
        <div className="flex items-center gap-2">
          <span>{t("viewers.rowsPerPage")}</span>
          <select
            className="rounded border border-white/20 bg-white/5 px-2 py-1 text-white"
            value={limit}
            onChange={(e) => onLimitChange(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4">
          <span className="tabular-nums">
            {pageStart.toLocaleString()}–{pageEnd.toLocaleString()} of{' '}
            {total.toLocaleString()}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={!canPrev}
              onClick={() => onOffsetChange(Math.max(0, offset - limit))}
              className="rounded border border-white/20 bg-white/5 px-2 py-1 text-white disabled:opacity-40"
            >{t("viewers.prev")}</button>
            <button
              type="button"
              disabled={!canNext}
              onClick={() => onOffsetChange(offset + limit)}
              className="rounded border border-white/20 bg-white/5 px-2 py-1 text-white disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {paneRow && hasContent && (
        <ContentPane
          row={paneRow}
          fields={contentFields!}
          title={contentTitle ? contentTitle(paneRow) : 'Detail'}
          onClose={() => setPaneRow(null)}
        />
      )}
    </div>
  );
}

function NestedCount({ value }: { value: unknown }) {
  const count = Array.isArray(value) ? value.length : 0;
  return (
    <span className="inline-flex items-center rounded-full bg-white/10 px-2 py-0.5 text-xs font-medium text-white/70">
      {count}
    </span>
  );
}
